/**
 * Shared ServiceOperation taxonomy + normalizer
 * (Service Contract Execution — Decision 13/13a, PRD §7).
 *
 * A ticket's `Service Type` + `Other Type` are free-text and polluted
 * ("Semi Annual" vs "Semi-Annual", "12 month", "Kohler", "Other"). Contracts,
 * by contrast, key coverage on a canonical `serviceOperation`. Raw string
 * matching ("PM3" vs "PM-3") is the #1 correctness risk for both C1 recognition
 * and G2 backfill.
 *
 * This module is the canonical vocabulary + normalizer for CONTRACT recognition
 * and backfill: `normalizeServiceOperations` maps raw ticket fields →
 * `ServiceOperation[]`, and C1 recognition + G2 backfill match on the canonical
 * value (never raw strings).
 *
 * NOTE (Decision 13 not yet fully realized): FSR pre-fill still uses the legacy
 * `serviceTypeMapping.ts` mapper, which outputs FSR checkbox booleans with UX
 * defaults (e.g. Maintenance→monthly) that intentionally differ from this
 * module's "no forced guess" contract. Migrating FSR pre-fill to derive from
 * this normalizer — so the two can never disagree — is a tracked follow-up.
 * Until then, treat THIS module as authoritative for contract matching only.
 *
 * Extensibility: add synonyms to the keyword tables below, not to call sites.
 * Unmatched input yields `[]` (⇒ human review) — NEVER a forced guess.
 *
 * NOTE: this file is mirrored between `functions/src/forms/serviceOperations.ts`
 * and `client/src/lib/forms/serviceOperations.ts`. Keep the two in sync.
 */

/**
 * Canonical service operations, grounded in 1,396 live `appsheet_service_logs`
 * (+ archive). OnPoint names PM by cadence, not PM#. The list is ordered — the
 * normalizer returns matches in this order for stable, deterministic output.
 */
export const SERVICE_OPERATIONS = [
  // PM (cadence-named)
  'pmMonthly',
  'pmQuarterly',
  'pmSemiAnnual',
  'pmAnnual',
  // Load bank (carries a durationHours attribute; see extractLoadBankDurationHours)
  'loadBankTest',
  // ATS (atsInspection is the seed; the rest are extensible / thin today)
  'atsInspection',
  'atsTransferTest',
  'insulationResistanceMegger',
  'fuelAlarmTesting',
  // Event ops
  'startUp',
  'plannedShutdown',
  'emergency',
  // Broad / non-contract (typically billable, not covered)
  'repair',
  'serviceCall',
  'install',
  'rental',
  'equipmentSale',
  'other',
] as const;

export type ServiceOperation = (typeof SERVICE_OPERATIONS)[number];

/**
 * Operations that are, by policy (PRD §7), NOT coverable by a contract — the
 * broad/billable buckets and emergency work. "Right unit, wrong operation =
 * billable": an emergency/repair on a PM-covered unit must never be treated as
 * covered. Contract authoring + C1 recognition consult COVERABLE_OPERATIONS so
 * this policy is enforced by the model, not by remembering a comment.
 */
export const NON_COVERABLE_OPERATIONS = [
  'emergency',
  'repair',
  'serviceCall',
  'install',
  'rental',
  'equipmentSale',
  'other',
] as const;

const NON_COVERABLE_SET: ReadonlySet<string> = new Set(NON_COVERABLE_OPERATIONS);

/** Operations a contract CAN cover (PM cadence, load bank, ATS, planned events). */
export const COVERABLE_OPERATIONS: readonly ServiceOperation[] = SERVICE_OPERATIONS.filter(
  (op) => !NON_COVERABLE_SET.has(op)
);

/** True when a contract may legitimately cover this operation. */
export function isCoverableOperation(op: ServiceOperation): boolean {
  return !NON_COVERABLE_SET.has(op);
}

const OPERATION_INDEX: ReadonlyMap<ServiceOperation, number> = new Map(
  SERVICE_OPERATIONS.map((op, i) => [op, i])
);

/** Type guard for canonical operations (rejects raw/unknown strings). */
export function isServiceOperation(value: unknown): value is ServiceOperation {
  return typeof value === 'string' && OPERATION_INDEX.has(value as ServiceOperation);
}

/**
 * Keyword → operation table applied to each parsed `Other Type` tag AND to the
 * `Service Type`. Order matters: more specific phrases come first so
 * "Semi Annual" beats "Annual" and "Transfer Test" beats generic "ATS". Only the
 * FIRST match per source string is taken, so one tag never yields two PM types.
 * All keywords are lowercase; matching is substring + case-insensitive.
 */
const OPERATION_KEYWORDS: ReadonlyArray<{ keyword: string; operation: ServiceOperation }> = [
  // PM cadence — specific before generic
  { keyword: 'semi annual', operation: 'pmSemiAnnual' },
  { keyword: 'semi-annual', operation: 'pmSemiAnnual' },
  { keyword: 'semiannual', operation: 'pmSemiAnnual' },
  // "biannual"/"bi-annual" in field-service usage = twice-yearly = semi-annual
  // (must precede the generic "annual" match below, which the string contains).
  { keyword: 'bi-annual', operation: 'pmSemiAnnual' },
  { keyword: 'bi annual', operation: 'pmSemiAnnual' },
  { keyword: 'biannual', operation: 'pmSemiAnnual' },
  { keyword: 'quarterly', operation: 'pmQuarterly' },
  { keyword: 'monthly', operation: 'pmMonthly' },
  { keyword: '12 month', operation: 'pmAnnual' },
  { keyword: '36 month', operation: 'pmAnnual' },
  { keyword: '72 month', operation: 'pmAnnual' },
  { keyword: 'annual', operation: 'pmAnnual' },
  // Load bank
  { keyword: 'load bank', operation: 'loadBankTest' },
  { keyword: 'load test', operation: 'loadBankTest' },
  // ATS — specific ops before the generic inspection
  { keyword: 'insulation resistance', operation: 'insulationResistanceMegger' },
  { keyword: 'megger', operation: 'insulationResistanceMegger' },
  { keyword: 'fuel alarm', operation: 'fuelAlarmTesting' },
  { keyword: 'transfer test', operation: 'atsTransferTest' },
  { keyword: 'ats functional', operation: 'atsTransferTest' },
  { keyword: 'full functional', operation: 'atsTransferTest' },
  { keyword: 'ats inspection', operation: 'atsInspection' },
  { keyword: 'ats visual', operation: 'atsInspection' },
  { keyword: 'ats pm', operation: 'atsInspection' },
  { keyword: 'ats service', operation: 'atsInspection' },
  { keyword: 'ats', operation: 'atsInspection' },
  // Event ops — `emergency` MUST precede the shutdown keywords so an
  // "Emergency Shutdown" is classified as emergency, not plannedShutdown.
  { keyword: 'emergency', operation: 'emergency' },
  { keyword: 'planned shutdown', operation: 'plannedShutdown' },
  { keyword: 'shutdown', operation: 'plannedShutdown' },
  { keyword: 'start up', operation: 'startUp' },
  { keyword: 'start-up', operation: 'startUp' },
  { keyword: 'startup', operation: 'startUp' },
  { keyword: 'commission', operation: 'startUp' },
];

/**
 * Broad `Service Type` categories → operation. Applied to `Service Type` only,
 * these are the non-PM buckets (repair/install/etc.). "Maintenance" intentionally
 * has no broad mapping — its operation comes from the PM cadence in `Other Type`.
 */
const SERVICE_TYPE_KEYWORDS: ReadonlyArray<{ keyword: string; operation: ServiceOperation }> = [
  { keyword: 'repair', operation: 'repair' },
  { keyword: 'service call', operation: 'serviceCall' },
  { keyword: 'install', operation: 'install' },
  { keyword: 'rental', operation: 'rental' },
  { keyword: 'equipment sale', operation: 'equipmentSale' },
  { keyword: 'sale', operation: 'equipmentSale' },
];

/** Split a comma-separated `Other Type` into trimmed, non-empty tags. */
function parseOtherType(otherType: string | undefined): string[] {
  if (!otherType) return [];
  return otherType
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

/** First keyword-table match for a single source string (or null). */
function firstMatch(
  source: string,
  table: ReadonlyArray<{ keyword: string; operation: ServiceOperation }>
): ServiceOperation | null {
  const text = source.toLowerCase();
  for (const { keyword, operation } of table) {
    if (text.includes(keyword)) return operation;
  }
  return null;
}

/**
 * Normalize a ticket's `Service Type` + `Other Type` to canonical operations.
 *
 * Both the contract's `contractService.serviceOperation` and a ticket's raw
 * fields resolve through THIS function, so recognition/backfill match on the
 * canonical value, never raw strings.
 *
 * @returns canonical operations in taxonomy order, deduped. Empty when nothing
 *          matched — the caller routes that to human review, never a guess.
 */
export function normalizeServiceOperations(
  serviceType: string | undefined,
  otherType: string | undefined
): ServiceOperation[] {
  const matched = new Set<ServiceOperation>();

  // Each Other Type tag contributes at most one operation (first match wins,
  // so "Semi Annual" resolves before "Annual").
  for (const tag of parseOtherType(otherType)) {
    const op = firstMatch(tag, OPERATION_KEYWORDS);
    if (op) matched.add(op);
  }

  // The Service Type itself may carry an operation (e.g. "Load Bank Test",
  // "Start Up", "Emergency") — scan it against the same table...
  if (serviceType) {
    const opFromType = firstMatch(serviceType, OPERATION_KEYWORDS);
    if (opFromType) matched.add(opFromType);
    // ...and against the broad non-PM categories.
    const broad = firstMatch(serviceType, SERVICE_TYPE_KEYWORDS);
    if (broad) matched.add(broad);
  }

  return Array.from(matched).sort(
    (a, b) => (OPERATION_INDEX.get(a) ?? 0) - (OPERATION_INDEX.get(b) ?? 0)
  );
}

/**
 * Extract a load-bank duration (in hours) from a ticket's `Other Type`
 * ("2 Hour Load Test" → 2; "30 min" → 0.5). Returns null when no duration is
 * present. Duration is an attribute of a `loadBankTest` operation, not its own
 * operation — the caller pairs it with the loadBankTest match.
 */
export function extractLoadBankDurationHours(otherType: string | undefined): number | null {
  if (!otherType) return null;
  const text = otherType.toLowerCase();

  const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?)\b/);
  const minMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?)\b/);
  if (!hourMatch && !minMatch) return null;

  // Sum both parts so a compound "1 Hour 30 Minutes" resolves to 1.5, not 1.
  const hours = hourMatch ? parseFloat(hourMatch[1]) : 0;
  const minutes = minMatch ? parseFloat(minMatch[1]) : 0;
  return hours + minutes / 60;
}
